// pages/master/menu/MasterMenuPage.jsx
'use client';

import { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Backdrop,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { useNotifier } from '../../../components/Notifier';
import { getModuleList, updateModuleMenuAll, deleteModuleMenu } from '../../../stores/roleModuleStore';
import MasterMenuAddEdit from './MasterMenuAddEdit';
import { getMenuIcon } from '../../../utils/iconMapperForMenu';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { useLanguage } from '../../../utils/i18n';

const flattenTree = (nodes, depth = 0, parentId = null) => {
  let result = [];
  (nodes || []).forEach((node) => {
    result.push({ ...node, depth, parentId, _collapsed: true });
    if (node.children && node.children.length > 0) {
      result = result.concat(flattenTree(node.children, depth + 1, node.id));
    }
  });
  return result;
};

const buildTreeFromFlat = (flatList) => {
  const root = [];
  const map = {};

  flatList.forEach((node) => {
    map[node.id] = { ...node, children: [] };
  });

  flatList.forEach((node) => {
    if (node.parentId && map[node.parentId]) {
      map[node.parentId].children.push(map[node.id]);
    } else {
      root.push(map[node.id]);
    }
  });

  return root;
};

export default function MasterMenuPage() {
  const notify = useNotifier();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);

  const [originalFlatData, setOriginalFlatData] = useState([]);
  const [flatData, setFlatData] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [projectedDepth, setProjectedDepth] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [parentTargetItem, setParentTargetItem] = useState(null);
  const [autoReorderLoading, setAutoReorderLoading] = useState(false);
  const [nameMenu, setNameMenu] = useState('Master Menu');

  // Bulk Delete State
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false);
  const [selectedForBulkDelete, setSelectedForBulkDelete] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const rowHeight = 56;
  const indentWidth = 32;

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const res = await getModuleList();
      const rawData = res?.data_modules || res?.data || res || [];
      setNameMenu(res?.name);
      const flat = flattenTree(rawData);
      setOriginalFlatData(JSON.parse(JSON.stringify(flat)));
      setFlatData(flat);
      setIsDirty(false);
    } catch {
      notify({ message: t('masterMenu.failedLoadMenu', 'Failed to load menu data'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddClick = () => {
    setEditingItem(null);
    setParentTargetItem(null);
    setOpenAddEditDialog(true);
  };

  const handleAddChildClick = (e, item) => {
    e.stopPropagation();
    setEditingItem(null);
    setParentTargetItem(item);
    setOpenAddEditDialog(true);
  };

  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditingItem(item);
    setOpenAddEditDialog(true);
  };

  const handleCloseAddEdit = () => {
    setOpenAddEditDialog(false);
    setEditingItem(null);
    setParentTargetItem(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmTargetName, setConfirmTargetName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (e, item) => {
    e.stopPropagation();
    setConfirmTargetId(item.id);
    setConfirmTargetName(item.alias || item.modules_name || item.module_name || t('masterMenu.thisItem', 'This item'));
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmTargetName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteModuleMenu(confirmTargetId);
      notify({ message: t('notify.deleteSuccess', 'Deleted successfully'), severity: 'success' });
      fetchMenu();
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
    }
  };

  const enterBulkDeleteMode = () => {
    setIsBulkDeleteMode(true);
    setFlatData((prev) => prev.map((item) => ({ ...item, _collapsed: false })));
  };

  const cancelBulkDeleteMode = () => {
    setIsBulkDeleteMode(false);
    setSelectedForBulkDelete([]);
    setFlatData(JSON.parse(JSON.stringify(originalFlatData)));
  };

  const handleOpenBulkConfirm = () => {
    setBulkConfirmOpen(true);
  };

  const handleCloseBulkConfirm = () => {
    if (bulkDeleteLoading) return;
    setBulkConfirmOpen(false);
  };

  const handleConfirmBulkDelete = async () => {
    setBulkDeleteLoading(true);
    let successCount = 0;
    try {
      for (const id of selectedForBulkDelete) {
        await deleteModuleMenu(id);
        successCount++;
      }
      notify({ message: t('masterMenu.bulkDeleteSuccess', `Successfully deleted ${successCount} items`), severity: 'success' });
      cancelBulkDeleteMode();
      fetchMenu();
    } catch (err) {
      console.error(err);
      notify({ message: t('masterMenu.bulkDeletePartialFailed', `Failed after deleting ${successCount} items`), severity: 'error' });
      fetchMenu();
      cancelBulkDeleteMode();
    } finally {
      setBulkDeleteLoading(false);
      setBulkConfirmOpen(false);
    }
  };

  const getDescendantCount = (data, index) => {
    let count = 0;
    const baseDepth = data[index].depth;
    for (let i = index + 1; i < data.length; i++) {
      if (data[i].depth > baseDepth) count++;
      else break;
    }
    return count;
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      const draggedRow = document.getElementById(`row-${flatData[index].id}`);
      if (draggedRow) draggedRow.style.opacity = '0.4';
    }, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const rowRect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rowRect.top;
    const offsetX = e.clientX - rowRect.left - 50;

    const isBottomHalf = offsetY > rowRect.height / 2;
    const targetDropIndex = isBottomHalf ? index + 1 : index;

    const childrenCount = getDescendantCount(flatData, draggedIndex);
    if (targetDropIndex > draggedIndex && targetDropIndex <= draggedIndex + childrenCount) {
      return;
    }

    let maxChildDepthOffset = 0;
    for (let i = draggedIndex + 1; i <= draggedIndex + childrenCount; i++) {
      const relDepth = flatData[i].depth - flatData[draggedIndex].depth;
      if (relDepth > maxChildDepthOffset) maxChildDepthOffset = relDepth;
    }

    let newDepth = Math.max(0, Math.floor(offsetX / indentWidth));
    const prevItem = targetDropIndex > 0 ? flatData[targetDropIndex - 1] : null;
    let maxDepth = prevItem ? prevItem.depth + 1 : 0;

    const globalMaxDepth = 2 - maxChildDepthOffset;
    if (maxDepth > globalMaxDepth) maxDepth = globalMaxDepth;

    if (newDepth > maxDepth) newDepth = maxDepth;

    if (dropIndex !== targetDropIndex || projectedDepth !== newDepth) {
      setDropIndex(targetDropIndex);
      setProjectedDepth(newDepth);
    }
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dropIndex !== null && projectedDepth !== null) {
      const newData = [...flatData];
      const draggedItem = newData[draggedIndex];
      const childrenCount = getDescendantCount(newData, draggedIndex);

      const itemsToMove = newData.splice(draggedIndex, 1 + childrenCount);

      const depthDiff = projectedDepth - draggedItem.depth;
      itemsToMove.forEach((item) => {
        item.depth += depthDiff;
      });

      let insertIndex = dropIndex;
      if (dropIndex > draggedIndex) {
        insertIndex -= 1 + childrenCount;
      }

      newData.splice(insertIndex, 0, ...itemsToMove);
      const reconstructedData = fixParentIds(newData);

      setFlatData(reconstructedData);
      setIsDirty(true);
    }

    if (draggedIndex !== null) {
      const draggedRow = document.getElementById(`row-${flatData[draggedIndex]?.id}`);
      if (draggedRow) draggedRow.style.opacity = '1';
    }

    setDraggedIndex(null);
    setDropIndex(null);
    setProjectedDepth(null);
  };

  const fixParentIds = (data) => {
    const stack = [];
    return data.map((item) => {
      while (stack.length > 0 && stack[stack.length - 1].depth >= item.depth) {
        stack.pop();
      }
      item.parentId = stack.length > 0 ? stack[stack.length - 1].id : null;
      stack.push(item);
      return item;
    });
  };

  const toggleCollapse = (e, index) => {
    e.stopPropagation();
    const newData = [...flatData];
    newData[index]._collapsed = !newData[index]._collapsed;
    setFlatData(newData);
  };

  const handleSaveReorder = async () => {
    setSaving(true);
    try {
      const nestedTree = buildTreeFromFlat(flatData);
      const payload = {
        data_modules: nestedTree,
        name: nameMenu || 'Master Menu'
      };

      await updateModuleMenuAll(payload);
      notify({ message: t('masterMenu.menuStructureUpdated', 'Menu structure updated successfully'), severity: 'success' });
      setOriginalFlatData(JSON.parse(JSON.stringify(flatData)));
      setIsDirty(false);
    } catch {
      notify({ message: t('masterMenu.failedSaveMenuStructure', 'Failed to save menu structure'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelReorder = () => {
    setFlatData(JSON.parse(JSON.stringify(originalFlatData)));
    setIsDirty(false);
  };

  const handleAutoReorderAfterAdd = async (parentItemTarget) => {
    if (!parentItemTarget) {
      await fetchMenu();
      return;
    }

    setAutoReorderLoading(true);
    try {
      // 1. Ambil list terbaru dari server (sudah termasuk menu baru tapi masih di root level)
      const res = await getModuleList();
      const rawData = res?.data_modules || res?.data || res || [];
      const newFlatList = flattenTree(rawData);

      // 2. Cari mana ID menu yang baru saja terbuat (ada di newFlatList tapi tidak ada di originalFlatData)
      const oldIds = new Set(originalFlatData.map((d) => d.id));
      const newlyAddedItem = newFlatList.find((d) => !oldIds.has(d.id));

      if (newlyAddedItem) {
        // 3. Ubah parentId menu baru tersebut ke parentItemTarget.id
        newlyAddedItem.parentId = parentItemTarget.id;

        // 4. Build nested tree dari newFlatList
        const nestedTree = buildTreeFromFlat(newFlatList);
        const payload = {
          data_modules: nestedTree,
          name: nameMenu || 'Master Menu'
        };

        // 5. Update ke server
        await updateModuleMenuAll(payload);
      }
    } catch (err) {
      console.error(err);
      notify({ message: t('masterMenu.failedAutoArrange', 'Failed to auto-arrange menu, please arrange manually.'), severity: 'warning' });
    } finally {
      // Refresh ulang semuanya untuk mendapatkan status terakurat
      await fetchMenu();
      setAutoReorderLoading(false);
    }
  };

  const getDisplayedData = () => {
    if (!searchQuery.trim()) return flatData;

    const lowerQuery = searchQuery.toLowerCase();

    const matchedNodes = flatData.filter((item) => (item.alias || item.modules_name || '').toLowerCase().includes(lowerQuery));

    const visibleIds = new Set();

    const addParentIds = (id) => {
      const node = flatData.find((n) => n.id === id);
      if (node) {
        visibleIds.add(node.id);
        if (node.parentId) {
          addParentIds(node.parentId);
        }
      }
    };

    matchedNodes.forEach((node) => addParentIds(node.id));

    return flatData.filter((item) => visibleIds.has(item.id));
  };

  const displayedData = getDisplayedData();
  const isSearching = !!searchQuery.trim();
  const disableInteractions = isSearching || isBulkDeleteMode;

  const renderRow = (item) => {
    const index = flatData.findIndex((d) => d.id === item.id);

    let isHidden = false;
    if (!isSearching) {
      let currentParentId = item.parentId;
      while (currentParentId !== null) {
        const parentNode = flatData.find((n) => n.id === currentParentId);
        if (parentNode) {
          if (parentNode._collapsed) {
            isHidden = true;
            break;
          }
          currentParentId = parentNode.parentId;
        } else {
          break;
        }
      }
    }

    if (isHidden) return null;

    const isActiveModule = item.is_active;
    const isDragged = draggedIndex === index;

    const hasChildren = flatData[index + 1] && flatData[index + 1].depth > item.depth;

    return (
      <Fragment key={item.id}>
        {dropIndex === index && draggedIndex !== null && !disableInteractions && (
          <Box
            sx={{
              height: rowHeight,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              border: '2px dashed #1976d2',
              borderRadius: 1,
              ml: `${(projectedDepth ?? 0) * indentWidth + 16}px`,
              mr: 2,
              my: 0.5,
              display: 'flex',
              alignItems: 'center',
              pl: 2,
              animation: 'expandDrop 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
              '@keyframes expandDrop': {
                '0%': { height: 0, opacity: 0, marginTop: 0, marginBottom: 0, borderWidth: 0 },
                '100%': { height: rowHeight, opacity: 1, marginTop: '4px', marginBottom: '4px', borderWidth: '2px' }
              }
            }}
          >
            <Typography variant="body2" color="primary" fontWeight="bold">
              {t('masterMenu.dropHere', 'Drop here...')}
            </Typography>
          </Box>
        )}

        <Grid
          id={`row-${item.id}`}
          container
          spacing={2}
          size={{ sm: 12, md: 12, lg: 12 }}
          draggable={!disableInteractions}
          onDragStart={(e) => !disableInteractions && handleDragStart(e, index)}
          onDragOver={(e) => !disableInteractions && handleDragOver(e, index)}
          onDragEnd={disableInteractions ? undefined : handleDragEnd}
          sx={{
            height: rowHeight,
            borderBottom: '1px solid #f0f0f0',
            bgcolor: isDragged ? '#e3f2fd' : '#ffffff',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            pl: `${item.depth * indentWidth + 16}px`,
            pr: 1,
            position: 'relative',
            cursor: disableInteractions ? 'default' : isDragged ? 'grabbing' : 'grab',
            ...(isDragged && {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 99
            }),
            '&:hover': { bgcolor: '#f8fafc' }
          }}
        >
          <Grid container alignItems="center" size={{ sm: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box
                sx={{
                  color: 'text.disabled',
                  display: 'flex',
                  alignItems: 'center',
                  mr: 1,
                  cursor: disableInteractions ? 'default' : 'inherit'
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>

              <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
                {hasChildren && (
                  <IconButton size="small" onClick={(e) => toggleCollapse(e, index)} disabled={disableInteractions}>
                    {item._collapsed && !disableInteractions ? (
                      <KeyboardArrowRightIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>

              <Box sx={{ color: 'primary.main', mr: 1.5, display: 'flex', alignItems: 'center' }}>
                {getMenuIcon(item.modules_icon, { fontSize: 'small' })}
              </Box>

              <Typography variant="body1" fontWeight={hasChildren ? 600 : 400}>
                {item.alias || item.modules_name}
              </Typography>
            </Box>
          </Grid>
          <Grid container alignItems="center" spacing={1} size={{ sm: 6.5 }}>
            {['read', 'create', 'update', 'delete', 'report'].map((feat) => {
              const isActive = item.features?.some((f) => f.name.toLowerCase() === (feat === 'update' ? 'edit' : feat) && f.status);
              const getFeatureColor = (f) => {
                switch (f) {
                  case 'read':
                    return '#2196f3';
                  case 'create':
                    return '#4caf50';
                  case 'update':
                  case 'edit':
                    return '#ff9800';
                  case 'delete':
                    return '#f44336';
                  case 'report':
                    return '#9c27b0';
                  default:
                    return 'primary.main';
                }
              };
              return (
                <FormControlLabel
                  key={feat}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!isActive}
                      sx={{
                        p: 0.5,
                        color: getFeatureColor(feat),
                        '&.Mui-checked': { color: getFeatureColor(feat) }
                      }}
                      disableRipple
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {feat}
                    </Typography>
                  }
                  sx={{ m: 0, pointerEvents: 'none' }}
                />
              );
            })}
            <FormControlLabel
              key={`active-${item.id}`}
              control={
                <Checkbox
                  size="small"
                  checked={isActiveModule}
                  sx={{
                    p: 0.5,
                    color: '#607d8b',
                    '&.Mui-checked': { color: '#607d8b' }
                  }}
                  disableRipple
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {t('masterRole.isActive', 'Is Active')}
                </Typography>
              }
              sx={{ m: 0, pointerEvents: 'none' }}
            />
          </Grid>
          <Grid container justifyContent="left" alignItems="center" spacing={1} size={{ sm: 1.5 }}>
            {isBulkDeleteMode ? (
              !hasChildren && (
                <Checkbox
                  checked={selectedForBulkDelete.includes(item.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedForBulkDelete([...selectedForBulkDelete, item.id]);
                    } else {
                      setSelectedForBulkDelete(selectedForBulkDelete.filter((id) => id !== item.id));
                    }
                  }}
                  color="error"
                />
              )
            ) : (
              <>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  sx={{ minWidth: 0, p: 0.5 }}
                  onClick={(e) => handleEditClick(e, item)}
                  title={t('masterMenu.editMenu', 'Edit Menu')}
                  disabled={isDirty}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </Button>
                {item.depth < 2 && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ minWidth: 0, p: 0.5 }}
                    onClick={(e) => handleAddChildClick(e, item)}
                    title={t('masterMenu.addChildMenu', 'Add Child Menu')}
                    disabled={isDirty}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
                  </Button>
                )}
                {!hasChildren && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ minWidth: 0, p: 0.5 }}
                    onClick={(e) => handleDelete(e, item)}
                    disabled={isDirty}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </Button>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  return (
    <Box sx={{ width: '100%', pb: 10 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderRadius: 1
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder={t('masterMenu.searchMenu', 'Search menu...')}
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 250, bgcolor: 'background.paper', borderRadius: 1, '& .MuiInputBase-root': { height: 40 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          {!isBulkDeleteMode && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disableElevation
              onClick={enterBulkDeleteMode}
              sx={{ height: 40, textTransform: 'none', fontWeight: 'bold' }}
              disabled={isDirty || loading}
            >
              {t('masterMenu.bulkDelete', 'Bulk Delete')}
            </Button>
          )}
          {!isBulkDeleteMode && (
            <Button
              variant="contained"
              color="info"
              startIcon={<AddIcon />}
              disableElevation
              onClick={handleAddClick}
              sx={{ height: 40, textTransform: 'none', fontWeight: 'bold' }}
              disabled={isDirty}
            >
              {t('masterMenu.addMasterMenu', 'Add Master Menu')}
            </Button>
          )}
        </Box>

        {isBulkDeleteMode && (
          <Box sx={{ display: 'flex', gap: 2, animation: 'fadeIn 0.3s ease' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={cancelBulkDeleteMode}
              disabled={bulkDeleteLoading}
              sx={{ textTransform: 'none' }}
            >
              {t('general.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={bulkDeleteLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
              onClick={handleOpenBulkConfirm}
              disabled={bulkDeleteLoading || selectedForBulkDelete.length === 0}
              disableElevation
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                px: 3,
                fontWeight: 700
              }}
            >
              {bulkDeleteLoading
                ? t('general.deleting', 'Deleting...')
                : t('masterMenu.deleteItems', `Delete Selected (${selectedForBulkDelete.length})`)}
            </Button>
          </Box>
        )}

        {isDirty && !isBulkDeleteMode && (
          <Box sx={{ display: 'flex', gap: 2, animation: 'fadeIn 0.3s ease' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={handleCancelReorder}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {t('general.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveReorder}
              disabled={saving}
              disableElevation
              sx={{
                backgroundColor: '#0072DA',
                textTransform: 'none',
                borderRadius: 1,
                px: 3,
                fontWeight: 700,
                boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
              }}
            >
              {saving ? t('general.saving', 'Saving...') : t('general.saveChanges', 'Save Changes')}
            </Button>
          </Box>
        )}
      </Box>

      <Paper variant="outlined" sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Grid container size={{ sm: 12, md: 12, lg: 12, xs: 12 }} sx={{ bgcolor: '#485D9A', borderBottom: '2px solid #e2e8f0', py: 1 }}>
          <Grid size={{ md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body1" sx={{ flex: 1, pl: 4, fontWeight: 'bold', color: '#ffffff' }}>
              {t('masterMenu.menuName', 'Menu Name')}
            </Typography>
          </Grid>
          <Grid size={{ md: 5 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body1" sx={{ width: 400, textAlign: 'center', fontWeight: 'bold', color: '#ffffff' }}>
              {t('masterMenu.features', 'Features')}
            </Typography>
          </Grid>
          <Grid size={{ md: 2 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body1" sx={{ width: 100, textAlign: 'center', fontWeight: 'bold', color: '#ffffff' }}>
              {t('general.actions', 'Action')}
            </Typography>
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {displayedData.map((item) => renderRow(item))}

            {dropIndex === flatData.length && draggedIndex !== null && !disableInteractions && (
              <Box
                sx={{
                  height: rowHeight,
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  border: '2px dashed #1976d2',
                  borderRadius: 1,
                  ml: `${(projectedDepth ?? 0) * indentWidth + 16}px`,
                  mr: 2,
                  my: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  pl: 2,
                  animation: 'expandDrop 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards'
                }}
              >
                <Typography variant="body2" color="primary" fontWeight="bold">
                  {t('masterMenu.dropHere', 'Drop here...')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {openAddEditDialog && (
        <MasterMenuAddEdit
          open={openAddEditDialog}
          onClose={handleCloseAddEdit}
          isEdit={!!editingItem}
          initialData={editingItem}
          parentTargetItem={parentTargetItem}
          onSuccess={handleAutoReorderAfterAdd}
        />
      )}
      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message={t('general.confirmDeleteItem', 'Are you sure you want to delete this item?')}
        itemName={confirmTargetName}
        confirmLabel={t('general.delete', 'Delete')}
        cancelLabel={t('general.cancel', 'Cancel')}
      />
      <DeleteConfirmDialog
        open={bulkConfirmOpen}
        loading={bulkDeleteLoading}
        onClose={handleCloseBulkConfirm}
        onConfirm={handleConfirmBulkDelete}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message={t('masterMenu.confirmDeleteSelected', 'Are you sure you want to delete the selected items?')}
        itemName={`${selectedForBulkDelete.length} items`}
        confirmLabel={t('general.delete', 'Delete')}
        cancelLabel={t('general.cancel', 'Cancel')}
      />

      <Backdrop sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column' }} open={autoReorderLoading}>
        <CircularProgress color="inherit" sx={{ mb: 2 }} />
        <Typography variant="h6" fontWeight="bold">
          {t('masterMenu.buildingAutoMenuStructure', 'Building an automatic menu structure...')}
        </Typography>
      </Backdrop>
    </Box>
  );
}
